import { useCallback, useEffect, useState } from "react";
import { Banner, Button, Layout } from "@shopify/polaris";
import { authenticatedFetch } from "../api";

export type BlockedReason =
	| "not_configured"
	| "no_subscription"
	| "subscription_inactive"
	| "unknown_plan"
	| "limit_reached"
	| "config_exceeds_plan";

export interface UsageResponse {
	billingExempt?: boolean;
	productsTranslated: number;
	productLimit: number;
	planName: string;
	charactersTranslated: number;
	currentMonth: string;
	blocked: boolean;
	blockedReason: BlockedReason | null;
	blockedMessage: string | null;
	subscriptionStatus: string | null;
}

export function useUsage() {
	const [usage, setUsage] = useState<UsageResponse | null>(null);

	const refresh = useCallback(async () => {
		try {
			const data = await authenticatedFetch<UsageResponse>("/api/usage");
			setUsage(data);
		} catch {
			// Silent fail — callers can still render without the banner.
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { usage, refresh };
}

interface EntitlementBannerProps {
	usage: UsageResponse | null;
	onViewPlans: () => void;
	onOpenConfiguration?: () => void;
}

const BLOCKED_TITLES: Record<BlockedReason, string> = {
	not_configured: "Choose your translation languages",
	no_subscription: "Choose a plan to start translating",
	subscription_inactive: "Your subscription is inactive",
	unknown_plan: "Your plan isn't recognised",
	limit_reached: "You've reached your monthly translation limit",
	config_exceeds_plan: "Your configuration exceeds your current plan",
};

export function EntitlementBanner({
	usage,
	onViewPlans,
	onOpenConfiguration,
}: EntitlementBannerProps) {
	if (!usage || !usage.blocked) return null;

	const reason = usage.blockedReason ?? "no_subscription";
	const title = BLOCKED_TITLES[reason] ?? BLOCKED_TITLES.no_subscription;

	// config_exceeds_plan is the only reason where fixing in Configuration is an
	// equal alternative to upgrading — surface both CTAs.
	const showConfigCta = reason === "config_exceeds_plan" && !!onOpenConfiguration;
	const upgradeLabel = reason === "limit_reached" ? "Upgrade plan" : "View plans";

	return (
		<Layout.Section>
			<Banner tone="warning" title={title}>
				<p>{usage.blockedMessage}</p>
				<div style={{ marginTop: 12, display: "flex", gap: 8 }}>
					<Button variant="primary" onClick={onViewPlans}>
						{upgradeLabel}
					</Button>
					{showConfigCta && (
						<Button onClick={onOpenConfiguration}>Open Configuration</Button>
					)}
				</div>
			</Banner>
		</Layout.Section>
	);
}
